import { useCallback, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { DamageType, EdgeMap, EncounterCommand, Room, RoomId, SquareCell, TerrainCell, TerrainMap, TileTypeId } from "@lancer-vtt/domain";
import type { EncounterDispatchResult } from "../../../lib/encounter/remoteEncounterController";
import {
  type CombatBrush,
  type CoverGrade,
  type Vertex,
  type WallHeight,
  roomBoxCommand,
  roomEraseCommand,
  tileBoxCommand,
  tileStrokeCommand,
  wallRunCommand,
} from "../combatAuthoring";

export type TileBoxDrag = { start: SquareCell; end: SquareCell; erase: boolean };
export type WallDrag = { from: Vertex; to: Vertex; erase: boolean };
export type RoomDrag = { start: SquareCell; end: SquareCell; erase: boolean };

export type AuthoringGestures = {
  strokeRef: RefObject<{ x: number; y: number }[] | null>;
  tilePreview: { cells: SquareCell[]; target: TerrainCell } | null;
  setTilePreview: Dispatch<SetStateAction<{ cells: SquareCell[]; target: TerrainCell } | null>>;
  hoverCell: SquareCell | null;
  setHoverCell: Dispatch<SetStateAction<SquareCell | null>>;
  finishStroke: () => void;
  tileBoxRef: RefObject<TileBoxDrag | null>;
  tileBoxPreview: TileBoxDrag | null;
  setTileBox: (drag: TileBoxDrag | null) => void;
  finishBox: () => void;
  wallRef: RefObject<WallDrag | null>;
  wallPreview: WallDrag | null;
  setWall: (drag: WallDrag | null) => void;
  finishWall: () => void;
  roomRef: RefObject<RoomDrag | null>;
  roomPreview: RoomDrag | null;
  setRoom: (drag: RoomDrag | null) => void;
  finishRoom: () => void;
};

/**
 * The three EDIT-mode authoring drags — the tile brush (rubber-band box by default, Shift-freehand
 * stroke), the rubber-band wall run, and the drag-a-box room gesture — each opened on a left press,
 * previewed live, and committed as ONE atomic `batch` (one undo entry). Every finalizer is
 * idempotent: the stage `onMouseUp` and the window-level off-canvas net both call it, and the first
 * one through nulls the ref.
 *
 * Each gesture keeps the same "authoritative ref + React state mirror" shape it always had: the ref
 * is written synchronously (so the off-canvas `mouseup` net reads the latest), the state drives the
 * preview render. The `*Ctx` refs carry the latest props into the finishers, which are stable
 * callbacks a window listener registered once can call without re-subscribing every render.
 */
export function useAuthoringGestures({
  brush,
  tile,
  coverGrade,
  hazard,
  eraseMode,
  terrain,
  edges,
  rooms,
  autoWall,
  wallHeight,
  wallCoverGrade,
  cols,
  rows,
  actorId,
  dispatch,
}: {
  brush: CombatBrush | null;
  /** The tile brush's picked catalogue tile. */
  tile: TileTypeId;
  coverGrade: CoverGrade;
  hazard: DamageType;
  eraseMode: boolean;
  terrain: TerrainMap;
  edges: EdgeMap;
  rooms: Record<RoomId, Room>;
  autoWall: boolean;
  wallHeight: WallHeight;
  wallCoverGrade: CoverGrade;
  cols: number;
  rows: number;
  actorId: string;
  dispatch: (command: EncounterCommand) => EncounterDispatchResult;
}): AuthoringGestures {
  // In-progress freehand tile stroke: the pointer samples (world px at scale 1) collected
  // since the press, or `null` when not stroking. A ref (not state) so sampling never re-renders.
  const strokeRef = useRef<{ x: number; y: number }[] | null>(null);
  // The live tile-stroke ghost: the deduped cells the open stroke will
  // paint plus the target it writes (direction fixed at press). Sampling stays in `strokeRef`
  // (authoritative, no re-render); this preview state is derived from it and updated only when the
  // covered-cell SET grows — a new cell entered — so a fast drag doesn't thrash React per-sample.
  const [tilePreview, setTilePreview] = useState<{ cells: SquareCell[]; target: TerrainCell } | null>(null);
  // The hover hint: the single cell under the cursor when a tile brush is armed
  // but no stroke is open — what a press there would start on. Cleared on pointer-leave / press.
  const [hoverCell, setHoverCell] = useState<SquareCell | null>(null);
  // The values the stroke finalizer needs, mirrored into a ref so the single window-level mouseup
  // listener (registered once) always reads the latest without re-subscribing every render.
  const strokeCtx = useRef({ brush, tile, coverGrade, hazard, eraseMode, terrain, cols, rows, actorId, dispatch });
  strokeCtx.current = { brush, tile, coverGrade, hazard, eraseMode, terrain, cols, rows, actorId, dispatch };

  // Commit the in-progress stroke as ONE atomic `batch` (one undo entry) and clear it. Idempotent:
  // the stage `onMouseUp` and the window safety-net both call it, but the first nulls the ref.
  // The sticky erase toggle swaps to the clear-everything `erase` brush (the Shift-freehand path's
  // erase route — Alt+Shift is deliberately avoided, so the toggle is how freehand erases).
  const finishStroke = useCallback(() => {
    const samples = strokeRef.current;
    strokeRef.current = null;
    setTilePreview(null); // drop the ghost on release (idempotent — a no-op if already cleared)
    if (!samples || samples.length === 0) return;
    const { brush, tile, coverGrade, hazard, eraseMode, terrain, cols, rows, actorId, dispatch } = strokeCtx.current;
    if (!brush) return;
    const cmd = tileStrokeCommand({ actorId, brush: eraseMode ? "erase" : brush, samples, tile, coverGrade, hazard, terrain, cols, rows });
    if (cmd) dispatch(cmd);
  }, []);

  // In-progress rubber-band tile BOX (the brushes' default gesture): the pressed start cell + live end cell + the Alt-erase flag captured at press,
  // mirrored into a ref (authoritative, synchronous for the off-canvas mouseup net) and React state
  // (drives the box preview + W×H readout). Mutually exclusive with the freehand stroke (Shift) —
  // only one of `strokeRef`/`tileBoxRef` is ever open. Direction is fixed at press (start cell or
  // the erase override), so a box never flickers paint-vs-clear mid-drag, exactly like the stroke.
  const tileBoxRef = useRef<TileBoxDrag | null>(null);
  const [tileBoxPreview, setTileBoxPreview] = useState<TileBoxDrag | null>(null);
  const setTileBox = useCallback((drag: TileBoxDrag | null) => {
    tileBoxRef.current = drag;
    setTileBoxPreview(drag);
  }, []);

  // Commit the in-progress tile box as ONE atomic `batch` of `set_terrain` children (one undo) and
  // clear it. Idempotent (the stage `onMouseUp` and the window net both call it; the first nulls the
  // ref). Direction is fixed by the press cell, or forced to clear by the captured Alt-erase flag.
  const finishBox = useCallback(() => {
    const drag = tileBoxRef.current;
    setTileBox(null);
    if (!drag) return;
    const { brush, tile, coverGrade, hazard, terrain, cols, rows, actorId, dispatch } = strokeCtx.current;
    if (!brush) return;
    const cmd = tileBoxCommand({ actorId, brush, start: drag.start, end: drag.end, erase: drag.erase, tile, coverGrade, hazard, terrain, cols, rows });
    if (cmd) dispatch(cmd);
  }, [setTileBox]);

  // In-progress rubber-band wall run: the pressed start vertex + the live end vertex,
  // mirrored into a ref (authoritative, updated synchronously so the off-canvas mouseup net reads
  // the latest) and React state (drives the preview line). The wall tool and the tile brush are
  // mutually exclusive, so only one of `strokeRef`/`wallRef` is ever open at a time.
  const wallRef = useRef<WallDrag | null>(null);
  const [wallPreview, setWallPreview] = useState<WallDrag | null>(null);
  const setWall = useCallback((drag: WallDrag | null) => {
    wallRef.current = drag;
    setWallPreview(drag);
  }, []);
  const wallCtx = useRef({ edges, cols, rows, actorId, dispatch, wallHeight, wallCoverGrade });
  wallCtx.current = { edges, cols, rows, actorId, dispatch, wallHeight, wallCoverGrade };

  // Commit the in-progress wall run as ONE atomic `batch` of `set_edge` children (one undo entry)
  // and clear it. Idempotent (the stage `onMouseUp` and the window net both call it; the first
  // nulls the ref). The direction (set vs clear/replace) is fixed by the first edge under the press
  // vertex against the armed `wallHeight` + (for low) `wallCoverGrade` target flags.
  const finishWall = useCallback(() => {
    const drag = wallRef.current;
    setWall(null);
    if (!drag) return;
    const { edges, cols, rows, actorId, dispatch, wallHeight, wallCoverGrade } = wallCtx.current;
    const cmd = wallRunCommand({
      actorId,
      from: drag.from,
      to: drag.to,
      edges,
      erase: drag.erase,
      height: wallHeight,
      coverGrade: wallCoverGrade,
      cols,
      rows,
    });
    if (cmd) dispatch(cmd);
  }, [setWall]);

  // In-progress drag-a-box room gesture: the pressed start cell + the live end cell,
  // mirrored into a ref (authoritative, synchronous for the off-canvas mouseup net) and React
  // state (drives the preview box). Mutually exclusive with the tile/wall gestures (only one tool
  // is armed at a time), so only one of `strokeRef`/`wallRef`/`roomRef` is ever open.
  const roomRef = useRef<RoomDrag | null>(null);
  const [roomPreview, setRoomPreview] = useState<RoomDrag | null>(null);
  const setRoom = useCallback((drag: RoomDrag | null) => {
    roomRef.current = drag;
    setRoomPreview(drag);
  }, []);
  const roomCtx = useRef({ rooms, edges, cols, rows, actorId, dispatch, autoWall });
  roomCtx.current = { rooms, edges, cols, rows, actorId, dispatch, autoWall };

  // Commit the in-progress room box as ONE atomic `batch` (one undo entry) and clear it. Idempotent
  // (the stage `onMouseUp` and the window net both call it; the first nulls the ref). With Alt held
  // (task 2.3) the box ERASES — `roomEraseCommand` subtracts its cells from whatever room(s) it
  // covers (shrink / carve-hole / auto-delete-on-zero); otherwise `roomBoxCommand` authors a room
  // (union-extend or create-new) and carves the contested cells out of every other room it overlaps
  // (no-overlap, task 2.2). Either way the result is one atomic batch off the live rooms map.
  const finishRoom = useCallback(() => {
    const drag = roomRef.current;
    setRoom(null);
    if (!drag) return;
    const { rooms, edges, cols, rows, actorId, dispatch, autoWall } = roomCtx.current;
    // Auto-wall folds perimeter/interior `set_edge` children into the SAME batch (one undo); when off
    // the gesture commits exactly today's room-only batch. The live `edges` are read to drop no-ops.
    const cmd = drag.erase
      ? roomEraseCommand({ actorId, start: drag.start, end: drag.end, rooms, cols, rows, autoWall, edges })
      : roomBoxCommand({ actorId, start: drag.start, end: drag.end, rooms, newId: crypto.randomUUID(), cols, rows, autoWall, edges });
    if (cmd) dispatch(cmd);
  }, [setRoom]);

  return {
    strokeRef,
    tilePreview,
    setTilePreview,
    hoverCell,
    setHoverCell,
    finishStroke,
    tileBoxRef,
    tileBoxPreview,
    setTileBox,
    finishBox,
    wallRef,
    wallPreview,
    setWall,
    finishWall,
    roomRef,
    roomPreview,
    setRoom,
    finishRoom,
  };
}
